/** Debe coincidir con functions/licenseHelpers.js (autoridad: evaluateLicenseState). */
export const LICENSE_GRACE_MS = 24 * 60 * 60 * 1000;

function paidUntilToMs(raw) {
  if (raw == null || raw === '') return null;
  try {
    if (typeof raw.toDate === 'function') {
      const d = raw.toDate();
      const ms = d.getTime();
      return Number.isFinite(ms) ? ms : null;
    }
    if (typeof raw === 'object' && typeof raw._seconds === 'number') {
      return raw._seconds * 1000 + Math.floor((raw._nanoseconds || 0) / 1e6);
    }
    if (typeof raw === 'object' && typeof raw.seconds === 'number') {
      return raw.seconds * 1000 + Math.floor((raw.nanoseconds || 0) / 1e6);
    }
    const ms = new Date(raw).getTime();
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
}

function isPendingActivation(lic) {
  if (!lic || typeof lic !== 'object') return false;
  if (lic.fullAccessGranted === true) return false;
  if (lic.accessRestricted === false) return false;
  if (lic.accessRestricted !== true) return false;
  const until = paidUntilToMs(lic.paidUntil);
  const now = Date.now();
  if (until != null && now <= until && lic.demo !== true) {
    return false;
  }
  return true;
}

/**
 * Proyección UI alineada con functions/licenseHelpers.evaluateLicenseState.
 */
export function evaluateLicenseUiState(lic) {
  if (!lic || typeof lic !== 'object') {
    return { phase: 'unpaid_needs_anchor', pagoUrl: null };
  }
  const pagoUrl = lic.pagoBilleteraUrl || lic.pago_billetera_url || null;
  if (lic.blocked === true) {
    return { phase: 'blocked', pagoUrl };
  }

  if (lic.trial === true) {
    const ends = paidUntilToMs(lic.trialEndsAt);
    const nowMs = Date.now();
    if (ends != null && nowMs <= ends) {
      return { phase: 'trial_active', trialEndsAtMs: ends, pagoUrl };
    }
    return { phase: 'trial_expired', trialEndsAtMs: ends, pagoUrl };
  }

  if (isPendingActivation(lic)) {
    return { phase: 'restricted_pending_activation', pagoUrl };
  }

  const until = paidUntilToMs(lic.paidUntil);
  if (until != null) {
    const now = Date.now();
    if (lic.demo === true) {
      if (now <= until) {
        return { phase: 'demo_active', paidUntilMs: until, pagoUrl };
      }
      return { phase: 'demo_expired', paidUntilMs: until, pagoUrl };
    }
    if (now <= until) {
      return { phase: 'active', paidUntilMs: until, pagoUrl };
    }
    const graceEnd = until + LICENSE_GRACE_MS;
    if (now <= graceEnd) {
      return { phase: 'grace', graceEndsAt: graceEnd, paidUntilMs: until, pagoUrl };
    }
    return { phase: 'expired', graceEndsAt: graceEnd, paidUntilMs: until, pagoUrl };
  }

  const anchor = paidUntilToMs(lic.unpaidGraceStartedAt);
  if (anchor == null) {
    return { phase: 'unpaid_needs_anchor', pagoUrl };
  }
  const graceEndUnpaid = anchor + LICENSE_GRACE_MS;
  const now = Date.now();
  if (now <= graceEndUnpaid) {
    return {
      phase: 'unpaid_grace',
      graceEndsAt: graceEndUnpaid,
      unpaidGraceStartedAtMs: anchor,
      pagoUrl
    };
  }
  return {
    phase: 'unpaid_expired',
    graceEndsAt: graceEndUnpaid,
    unpaidGraceStartedAtMs: anchor,
    pagoUrl
  };
}

/** Overlay UX (SPEC 001A): plan elegido, pago pendiente — no es fase motor nueva. */
export function getTrialJourneyOverlay(lic, ui) {
  if (!ui || ui.phase !== 'trial_expired') return null;
  const chosen = lic?.chosenPlan || lic?.mercadopagoBillingPlan;
  if (chosen) return 'restricted_pending_payment';
  return 'ux_trial_expired_gate';
}

export function shouldShowTrialContinuationGate(ui) {
  return ui?.phase === 'trial_expired';
}

export function formatGraceCountdown(graceEndsAtMs) {
  if (!graceEndsAtMs) return '';
  const sec = Math.max(0, Math.floor((graceEndsAtMs - Date.now()) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h} h ${m} min`;
}

/** Días completos hasta paidUntil / trialEndsAt (puede ser negativo si ya venció). */
export function daysUntilPaidUntil(paidUntilMs) {
  if (paidUntilMs == null) return null;
  return Math.ceil((paidUntilMs - Date.now()) / (24 * 60 * 60 * 1000));
}

export function trialDaysUsed(lic) {
  const start = paidUntilToMs(lic?.trialStartedAt);
  const end = paidUntilToMs(lic?.trialEndsAt);
  if (start == null) return null;
  const until = Math.min(Date.now(), end != null ? end : Date.now());
  const days = Math.max(1, Math.ceil((until - start) / (24 * 60 * 60 * 1000)));
  return days;
}
